import React, { useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import { FileType } from "../../../../redux/types/Project";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import axios from "axios";
import {
  CREATE_UPLOAD_URL,
  CREATE_PROJECT_FILE,
} from "../../../../apolloClient/operation/mutations/mutation";

interface ProjectMeasurementProps {
  project: CustomerProjectDetails;
  refetchData: Function;
  permissions?: boolean;
}

interface CreateUploadFileInput {
  contentType: string;
}

interface CreateProjectFileInput {
  projectId: number;
  url: string;
  type: string;
}

interface UploadImageResponse {
  success: boolean;
  error?: any;
}

const ProjectMeasurement: React.FC<ProjectMeasurementProps> = ({
  project,
  refetchData,
  permissions = true,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [getUploadUrl] = useMutation(CREATE_UPLOAD_URL);
  const [uploadFile] = useMutation(CREATE_PROJECT_FILE);

  // Filter measurement documents
  const measurementDocuments =
    project?.documents?.filter((doc) => doc.type === FileType.MEASUREMENT) ||
    [];

  // Check if measurement phase is approved (progress step 2)
  const measurementProgress = project?.progress?.find((p) => p.step === 2);
  const isMeasurementApproved = measurementProgress?.approved || false;

  // Upload utility function
  const uploadImage = async (
    uploadUrl: string,
    file: File,
    contentType: string
  ): Promise<UploadImageResponse> => {
    try {
      const options = {
        headers: {
          "Content-Type": contentType,
          "X-Amz-Acl": "public-read",
          "Content-Disposition": "inline",
        },
      };
      const response = await axios.put(uploadUrl, file, options);

      if (response.status === 200) {
        return { success: true };
      }
    } catch (error) {
      console.error("Error uploading file", error);
      return { success: false, error };
    }

    return { success: false };
  };

  // Handle file selection and upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    setIsUploading(true);
    try {
      const file = event.target.files[0];

      // Validate file type
      if (file.type !== "application/pdf") {
        toast.error("Please upload PDF files only");
        setIsUploading(false);
        return;
      }

      const input: CreateUploadFileInput = { contentType: file.type };
      const { data } = await getUploadUrl({ variables: { input } });

      if (data) {
        const createFileInput: CreateProjectFileInput = {
          projectId: project.projectId,
          url: data.createUploadUrl.fileName,
          type: FileType.MEASUREMENT,
        };

        const result = await uploadImage(
          data.createUploadUrl.url,
          file,
          file.type
        );

        if (result.success) {
          const success = await uploadFile({
            variables: { input: createFileInput },
          });

          if (success) {
            toast.success("Document uploaded successfully");
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
            refetchData();
          } else {
            toast.error("Failed to save document. Please try again.");
          }
        } else {
          toast.error("Failed to upload document. Please try again.");
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("An error occurred during upload. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDocumentClick = (url: string) => {
    window.open(url, "_blank");
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Customer Request Status - Only show if NOT approved */}
      {project?.MeasurementSupportRequested && !isMeasurementApproved && (
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            backgroundColor: "#eff6ff",
            borderLeft: "4px solid #3b82f6",
            borderRadius: "6px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#3b82f6",
              }}
            />
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#1e40af",
              }}
            >
              Measurement Support Requested
            </Typography>
          </Box>
          {project?.MeasurementRemarks && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#6b7280",
                  mb: 0.5,
                }}
              >
                Customer Remarks:
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: "#374151",
                  whiteSpace: "pre-wrap",
                }}
              >
                {project.MeasurementRemarks}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Status Badge */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: "#6b7280",
            mb: 1,
          }}
        >
          Current Status
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: "6px",
            backgroundColor: isMeasurementApproved
              ? "#d1fae5"
              : measurementDocuments.length > 0
              ? "#fef3c7"
              : "#f3f4f6",
            border: `1px solid ${
              isMeasurementApproved
                ? "#a7f3d0"
                : measurementDocuments.length > 0
                ? "#fde68a"
                : "#e5e7eb"
            }`,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: isMeasurementApproved
                ? "#10b981"
                : measurementDocuments.length > 0
                ? "#f59e0b"
                : "#9ca3af",
            }}
          />
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: isMeasurementApproved
                ? "#065f46"
                : measurementDocuments.length > 0
                ? "#92400e"
                : "#4b5563",
            }}
          >
            {isMeasurementApproved
              ? "Approved"
              : measurementDocuments.length > 0
              ? "Documents Uploaded but review pending"
              : project?.MeasurementSupportRequested
              ? "Request Pending"
              : "No Documents"}
          </Typography>
        </Box>
      </Box>

      {/* Upload Section */}
      {permissions && (
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: "#1f2937",
              mb: 2,
            }}
          >
            Upload Measurement Documents
          </Typography>
          <Box
            sx={{
              position: "relative",
              border: "2px dashed #d1d5db",
              borderRadius: "8px",
              p: 4,
              textAlign: "center",
              backgroundColor: "#f9fafb",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileUpload}
              disabled={isUploading}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: isUploading ? "not-allowed" : "pointer",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6b7280"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#374151",
                }}
              >
                {isUploading ? "Uploading..." : "Click to upload or drag and drop"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#6b7280",
                }}
              >
                PDF files only
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Documents List */}
      <Box>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: "#1f2937",
            mb: 2,
          }}
        >
          Measurement Documents ({measurementDocuments.length})
        </Typography>

        {measurementDocuments.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              backgroundColor: "#f9fafb",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                color: "#6b7280",
              }}
            >
              No measurement documents uploaded yet
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 2,
            }}
          >
            {measurementDocuments.map((doc) => (
              <Box
                key={doc.id}
                onClick={() => handleDocumentClick(doc.url)}
                sx={{
                  p: 2.5,
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#4b87ed",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {/* PDF Icon */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "6px",
                      backgroundColor: "#fee2e2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#dc2626",
                      }}
                    >
                      PDF
                    </Typography>
                  </Box>

                  {/* Document Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#1f2937",
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {doc.fileName || `Document #${doc.id}`}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#6b7280",
                      }}
                    >
                      Uploaded on {formatDate(doc.createdAt)}
                    </Typography>
                  </Box>

                  {/* View Icon */}
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "6px",
                      backgroundColor: "#f3f4f6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#6b7280"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProjectMeasurement;
